import { useGraphqlQuery } from "./useGraphqlQuery"
import { NewsletterList, Query } from "common/types/graphql"
import { gql } from "common/lib/gql"
import { useCallback } from "react"
import { hasActiveSession } from "common/lib/cookies"

export const EMAILS_QUERY = gql`
  query {
    viewer {
      user {
        emails {
          email
          primary
          newsletterSubscriptions {
            subscribed
            newsletterList {
              id
              frequency
              name
              pitch
              region {
                code
              }
            }
          }
        }
      }
    }
  }
`

/**
 * Data hook to return the current viewer newsletter subscriptions data.
 *
 * Newsletter subscriptions are only related to their primary email address.
 */
export const useViewerSubscriptions = () => {
  const { data } = useGraphqlQuery<Query>(hasActiveSession() ? EMAILS_QUERY : null)

  const getPrimaryEmail = useCallback(
    () => data?.viewer?.user?.emails?.find((email) => email.primary),
    [data?.viewer?.user?.emails],
  )

  const getPrimaryEmailAddress = useCallback(
    () => getPrimaryEmail()?.email || "",
    [getPrimaryEmail],
  )

  const getNewsletterSubscriptions = useCallback(
    () => getPrimaryEmail()?.newsletterSubscriptions?.filter(({ subscribed }) => subscribed) || [],
    [getPrimaryEmail],
  )

  // Go through all the users emails and get all subscribed newsletters from each
  const getAllSubscribedNewsletters = useCallback(() => {
    const emails = data?.viewer?.user?.emails || []

    return emails.reduce((acc: NewsletterList[], email) => {
      email.newsletterSubscriptions?.forEach(({ subscribed, newsletterList }) => {
        if (subscribed && newsletterList && !acc.some(({ id }) => id === newsletterList.id)) {
          acc.push(newsletterList)
        }
      })

      return acc
    }, [])
  }, [data?.viewer?.user?.emails])

  return {
    getNewsletterSubscriptions,
    getPrimaryEmailAddress,
    getAllSubscribedNewsletters,
  }
}
